import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRightLeft, Gift, Swords, Users, Check, X } from "lucide-react";
import { useLocale } from "@/hooks/useLocale";

const TYPE_ICONS: Record<string, any> = {
  trade_offer: ArrowRightLeft,
  gift: Gift,
  challenge: Swords,
  alliance_request: Users,
};

interface Props {
  interaction: any;
  currentUserId: string | null;
  getPlayerName: (userId: string) => string;
  onRespond: (interaction: any, status: "accepted" | "declined") => void;
  onCancel: (interaction: any) => void;
}

export function InteractionCard({ interaction: ix, currentUserId, getPlayerName, onRespond, onCancel }: Props) {
  const { t } = useLocale();
  const Icon = TYPE_ICONS[ix.type] || ArrowRightLeft;
  const isFrom = ix.from_user_id === currentUserId;
  const isPending = ix.status === "pending";

  return (
    <div className="rounded-md border border-border p-2 text-xs">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3 h-3 text-primary shrink-0" />
        <span className="font-medium capitalize">{ix.type.replace("_", " ")}</span>
        <Badge variant={
          ix.status === "accepted" ? "default" :
          ix.status === "declined" ? "destructive" :
          ix.status === "cancelled" ? "secondary" : "outline"
        } className="text-[9px] ml-auto">
          {ix.status}
        </Badge>
      </div>
      <div className="text-muted-foreground">
        {getPlayerName(ix.from_user_id)} → {getPlayerName(ix.to_user_id)}
      </div>
      {ix.payload?.message && (
        <div className="mt-1 text-foreground italic">"{ix.payload.message}"</div>
      )}
      {ix.payload?.offered_items?.length > 0 && (
        <div className="mt-0.5">{t.mp_offering}: {ix.payload.offered_items.join(", ")}</div>
      )}
      {ix.payload?.requested_items?.length > 0 && (
        <div className="mt-0.5">{t.mp_requesting}: {ix.payload.requested_items.join(", ")}</div>
      )}
      {isPending && !isFrom && ix.to_user_id === currentUserId && (
        <div className="flex gap-1 mt-1.5">
          <Button size="sm" className="h-6 text-[10px] gap-1" onClick={() => onRespond(ix, "accepted")}>
            <Check className="w-2.5 h-2.5" /> {t.play_accept}
          </Button>
          <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={() => onRespond(ix, "declined")}>
            <X className="w-2.5 h-2.5" /> {t.mp_decline}
          </Button>
        </div>
      )}
      {isPending && isFrom && (
        <Button size="sm" variant="ghost" className="h-6 text-[10px] mt-1" onClick={() => onCancel(ix)}>
          {t.cancel}
        </Button>
      )}
    </div>
  );
}
